import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { linkProps } from '../../../utils/appLink';
import {
  FOOTER_CATEGORY_LINK_GROUPS,
  CATEGORY_LANDING_HREFS,
} from '../../../utils/productNavigation';
import Image from '../../Image/Image';

const HIPPO_FOOTER =
  'https://assets.askhippo.co.za/Pages/Footer/HippoFooter.svg';

const SOCIAL_ICONS = {
  facebook: 'https://assets.askhippo.co.za/Pages/Footer/Facebook.svg',
  linkedin: 'https://assets.askhippo.co.za/Pages/Footer/LinkedIn.svg',
  x: 'https://assets.askhippo.co.za/Pages/Footer/X.png',
  tiktok: 'https://assets.askhippo.co.za/Pages/Footer/TikTok.svg',
  youtube: 'https://assets.askhippo.co.za/Pages/Footer/YouTube.svg',
  instagram: 'https://assets.askhippo.co.za/Pages/Footer/Instagram.svg',
};

const socialLinks = [
  {
    icon: SOCIAL_ICONS.facebook,
    url: 'https://www.facebook.com/HippoSA',
    alt: 'Hippo on Facebook',
    title: 'Follow Hippo on Facebook',
  },
  {
    icon: SOCIAL_ICONS.linkedin,
    url: 'https://www.linkedin.com/company/hippo-comparative-services-pty-ltd',
    alt: 'Hippo on LinkedIn',
    title: 'Follow Hippo on LinkedIn',
  },
  {
    icon: SOCIAL_ICONS.x,
    url: 'https://twitter.com/hippo-co-za',
    alt: 'Hippo on X (Twitter)',
    title: 'Follow Hippo on X (Twitter)',
  },
  {
    icon: SOCIAL_ICONS.tiktok,
    url: 'https://www.tiktok.com/@hippo_co_za?',
    alt: 'Hippo on TikTok',
    title: 'Follow Hippo on TikTok',
  },
  {
    icon: SOCIAL_ICONS.youtube,
    url: 'https://www.youtube.com/@hippo_co_za?',
    alt: 'Hippo on YouTube',
    title: 'Subscribe to Hippo on YouTube',
  },
  {
    icon: SOCIAL_ICONS.instagram,
    url: 'https://www.instagram.com/hippo.co.za',
    alt: 'Hippo on Instagram',
    title: 'Follow Hippo on Instagram',
  },
];

const textBodyStyle = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  letterSpacing: '-0.002em',
  color: '#738289',
  whiteSpace: 'pre-line',
};

const topRowLinkGroups = [
  {
    title: 'Company',
    links: [
      { label: 'About', href: '/about-us/' },
      { label: 'Blog', href: '/blog/' },
      {
        label: 'Contact Us',
        href: '/contact-us/',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      { label: 'Site Map', href: '/sitemap/' },
    ],
  },
  {
    title: 'Policies',
    links: [
      {
        label: 'Terms & Conditions',
        href: '/legal/terms-and-conditions/',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      {
        label: 'Privacy Policy',
        href: '/more/privacypolicy2024.pdf',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      {
        label: 'PAIA Manual',
        href: '/more/PAIA-manual-2026.pdf',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      { label: 'Marketing', href: '/newsletter-updates/' },
      {
        label: 'Conflict of Interest',
        href: '/more/Conflict_of_Interest_Management_Policy_2024.pdf',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    ],
  },
];

const LinkCard = ({ title, titleHref, links }) => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    }}
  >
    <Typography
      component={titleHref ? 'a' : 'p'}
      href={titleHref}
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 700,
        fontSize: '1rem',
        lineHeight: '1.5rem',
        letterSpacing: '-0.02em',
        color: '#162F3B',
        mb: '8px',
        textDecoration: 'none',
        ...(titleHref && {
          '&:hover': {
            color: '#C7CA00',
            textDecoration: 'underline',
          },
        }),
      }}
    >
      {title}
    </Typography>

    {links.map(({ label, href }) => (
      <Typography
        key={label}
        {...linkProps(href)}
        sx={{
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '-0.002em',
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          color: '#738289',
          textDecoration: 'none',
          '&:hover': {
            color: '#C7CA00',
            fontWeight: 700,
            textDecoration: 'underline',
          },
        }}
      >
        {label}
      </Typography>
    ))}
  </Box>
);

const MEDICAL_AID_DISCLAIMER =
  '*This Medical Aid comparison is powered by Hippo Advisory Services (Pty) Ltd FSP No. 36088. Please note that this service is not available to intermediaries. This article is for informational purposes only and should not be construed as financial, legal or medical advice.';

const KAUFMAN_DISCLAIMER =
  '*Based on independent research by Kaufman Levin & Associates 2025.';

const FooterComponent = () => {
  const { pathname } = useLocation();
  const isMedicalAid = pathname.includes('medical-aid-quote');
  const disclaimer = isMedicalAid ? MEDICAL_AID_DISCLAIMER : KAUFMAN_DISCLAIMER;

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #EEF2F4',
        boxShadow: '0px -12px 24px -12px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        sx={{
          maxWidth: '1220px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 4, lg: 6 },
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: { xs: 4, lg: 6 },
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: { xs: '100%', lg: '381px' },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box sx={{ maxWidth: '213px', maxHeight: '40px' }}>
                <Image
                  src={HIPPO_FOOTER}
                  alt="Hippo comparison service logo"
                  title="Hippo - Trusted insurance comparison service"
                  width={213}
                  height={40}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: { xs: '100%', lg: '381px' },
                }}
              >
                <Typography sx={textBodyStyle}>
                  Hippo Comparative Services (Pty) Ltd is an authorised
                  financial service provider.
                </Typography>

                <Typography sx={textBodyStyle}>
                  FSP number: 16357. Block A, Steyn City Capital Park, Erling
                  Road, Riverglen, Dainfern, 2191
                </Typography>

                <Typography sx={textBodyStyle}>{disclaimer}</Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {socialLinks.map(({ icon, url, alt, title }) => (
                  <Box
                    key={alt}
                    {...linkProps(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: '50%',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Image
                      src={icon}
                      alt={alt}
                      title={title}
                      width={30}
                      height={30}
                      sx={{ width: '30px', height: '30px' }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                gap: { xs: 4, md: 6 },
                flexWrap: 'wrap',
                alignItems: 'flex-start',
              }}
            >
              {topRowLinkGroups.map((group) => (
                <Box
                  key={group.title}
                  sx={{
                    minWidth: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                      mb: '8px',
                    }}
                  >
                    {group.title}
                  </Typography>

                  {group.links.map(({ label, href, target, rel }) => (
                    <Typography
                      key={label}
                      {...linkProps(href, { target, rel })}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        letterSpacing: '-0.002em',
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#738289',
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#C7CA00',
                          fontWeight: 700,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              width: '100%',
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
                lg: 'repeat(7, minmax(0, 1fr))',
              },
              columnGap: { xs: 3, md: 4 },
              rowGap: { xs: 4, md: 5 },
              alignItems: 'start',
            }}
          >
            {FOOTER_CATEGORY_LINK_GROUPS.map((group) => (
              <LinkCard
                key={group.title}
                title={group.title}
                titleHref={CATEGORY_LANDING_HREFS[group.title]}
                links={group.links}
              />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            pt: 2,
            borderTop: '1px solid #EEF2F4',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Overline, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: '1.25rem',
              letterSpacing: '-0.01em',
              color: '#738289',
              textAlign: 'center',
            }}
          >
            © Copyright {new Date().getFullYear()} Hippo Comparative Services
            (Pty) Ltd. All Rights Reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FooterComponent;
